import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ListFilter, Search } from 'lucide-react';
import { BoardSearchPickerShell } from './boardSearchPickerShell';

export function BoardSearchSimplePickerPanel({
  searchPlaceholder = 'Buscar',
  searchTerm = '',
  onSearchChange,
  items = [],
  getKey,
  getLabel,
  getSuffix,
  /** Optional: return group label to render sections (like UF heads in cidade picker). */
  getItemGroup,
  /** Optional: custom group header label node (icons, etc.). */
  renderGroupLabel,
  /** Show count badge on group heads. Default true. */
  showGroupCount = true,
  showSearch = true,
  isItemDisabled,
  isSelected,
  selectedKeys = [],
  onToggle,
  emptyMessage = 'Nenhum resultado',
  listAriaLabel = 'Opções',
  headerBanner = null,
  bulkMenuEnabled = false,
  bulkExclusive = false,
  onBulkToggleAll,
}) {
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkAnchorRef = useRef(null);
  const selectedSet = new Set(selectedKeys);
  const resolveSelected = (item) =>
    isSelected ? isSelected(item) : selectedSet.has(getKey(item));

  const selectableItems = useMemo(
    () => items.filter((item) => !isItemDisabled?.(item)),
    [items, isItemDisabled]
  );

  const allSelectableMarked = useMemo(() => {
    if (selectableItems.length === 0) return false;
    if (bulkExclusive) {
      return selectableItems.some((item) => resolveSelected(item));
    }
    return selectableItems.every((item) => resolveSelected(item));
  }, [selectableItems, selectedKeys, isSelected, bulkExclusive]);

  useEffect(() => {
    if (!bulkMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (bulkAnchorRef.current?.contains(event.target)) return;
      setBulkMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [bulkMenuOpen]);

  const handleBulkToggle = () => {
    onBulkToggleAll?.();
    setBulkMenuOpen(false);
  };

  const groupedItems = useMemo(() => {
    if (typeof getItemGroup !== 'function' || items.length === 0) return null;
    const order = [];
    const map = new Map();
    items.forEach((item) => {
      const group = String(getItemGroup(item) || '').trim() || '—';
      if (!map.has(group)) {
        map.set(group, []);
        order.push(group);
      }
      map.get(group).push(item);
    });
    return order.map((label) => ({ label, items: map.get(label) }));
  }, [items, getItemGroup]);

  const renderSuffix = (item) => {
    const suffix = getSuffix?.(item);
    if (suffix == null || suffix === '') return null;
    if (React.isValidElement(suffix)) {
      return <span className="bsf-simple-picker-item-meta-slot">{suffix}</span>;
    }
    return <span className="bsf-simple-picker-item-meta">{suffix}</span>;
  };

  const renderItem = (item) => {
    const key = getKey(item);
    const selected = resolveSelected(item);
    const disabled = isItemDisabled?.(item);
    return (
      <button
        key={key}
        type="button"
        role="option"
        aria-selected={selected}
        disabled={disabled}
        className={`bsf-simple-picker-item${selected ? ' is-selected' : ''}${
          disabled ? ' is-disabled' : ''
        }${groupedItems ? ' bsf-simple-picker-item--city' : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          if (!disabled) onToggle(item);
        }}
      >
        <span className="bsf-simple-picker-item-label" title={getLabel(item)}>
          {getLabel(item)}
        </span>
        {renderSuffix(item)}
        <Check size={12} strokeWidth={2.5} className="bsf-simple-picker-check" aria-hidden />
      </button>
    );
  };

  return (
    <BoardSearchPickerShell>
      {headerBanner ? <div className="bsf-picker-banner">{headerBanner}</div> : null}
      {showSearch ? (
        <div className="bsf-simple-picker-search">
          <Search size={13} strokeWidth={2} className="bsf-simple-picker-search-icon" aria-hidden />
          <input
            type="text"
            className="bsf-simple-picker-input"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            autoFocus
          />
          {bulkMenuEnabled ? (
          <div className="bsf-simple-picker-bulk-anchor" ref={bulkAnchorRef}>
            <button
              type="button"
              className="bsf-simple-picker-bulk-trigger"
              aria-label="Mais opções"
              aria-expanded={bulkMenuOpen}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                setBulkMenuOpen((open) => !open);
              }}
            >
              <ListFilter size={12} strokeWidth={2} aria-hidden />
            </button>
            {bulkMenuOpen ? (
              <div className="bsf-simple-picker-bulk-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="bsf-simple-picker-bulk-menu-item"
                  disabled={selectableItems.length === 0}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleBulkToggle}
                >
                  {allSelectableMarked ? 'Desmarcar tudo' : 'Selecionar tudo'}
                </button>
              </div>
            ) : null}
          </div>
          ) : null}
        </div>
      ) : null}
      <div className="bsf-simple-picker-list" role="listbox" aria-label={listAriaLabel}>
        {items.length > 0 ? (
          groupedItems ? (
            groupedItems.map(({ label, items: groupItems }) => (
              <div key={label} className="bsf-simple-picker-group">
                <div
                  className="bsf-simple-picker-item bsf-simple-picker-item--uf-head"
                  role="presentation"
                >
                  <span className="bsf-simple-picker-item-label">
                    {typeof renderGroupLabel === 'function' ? renderGroupLabel(label) : label}
                  </span>
                  {showGroupCount ? (
                    <span className="bsf-simple-picker-item-meta">{groupItems.length}</span>
                  ) : null}
                </div>
                {groupItems.map(renderItem)}
              </div>
            ))
          ) : (
            items.map(renderItem)
          )
        ) : (
          <div className="bsf-simple-picker-empty">{emptyMessage}</div>
        )}
      </div>
    </BoardSearchPickerShell>
  );
}
